import FJEditFloatTool from '../view/FJEditFloatTool';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MOUSE_EVENT_TYPE, FJViewCanvasSingleton } from '../js/FJViewCanvas';
import FJActionBrushEditCommon from '../../../../redux/action/FJActionBrushEditCommon';
import { memo } from 'react';
import { setSliceIsMouseMove } from '../../../../redux/slices/FJSliceBrushEditCommon';
import { useMemo } from 'react';
import useLocalStore from '../../../../hooks/useLocalStore';
import FJAppConstant from '../../../../constants/FJAppConstant';

const FJEditFloatToolContainer = memo(function FJEditFloatToolContainer(props) {
    useLocalStore();
    const dispatch = useDispatch();
    const { isShowEditCanvas, setMouseInToolPanel } = props;
    const zoom = useSelector(state => state.brushEditCommon.zoom);
    const curBrushType = useSelector(state => state.brushEditCommon.curBrushType);
    const hasBrushUndo = useSelector(state => state.brushEditCommon.hasBrushUndo);
    const hasBrushRedo = useSelector(state => state.brushEditCommon.hasBrushRedo);
    const _disableReset = useSelector(state => state.brushEditCommon.disableReset);
    const isMouseMove = useSelector(state => state.brushEditCommon.isMouseMove);
    const disableCompare = useSelector(state => state.brushEditCommon.disableCompare);
    const isSlideCompare = useSelector(state => state.brushEditCommon.isSlideCompare);
    const disableFitButton = useSelector(state => state.brushEditCommon.disableFitButton);
    const isPhotoRemoveBg = useSelector(state => state.brushEditCommon.isPhotoRemoveBg);
    const appType = useSelector(state => state.common.appType);
    const brushSize = useSelector(state => state.brushEditCommon.brushSize);

    // 如果当前不是画笔状态，则隐藏相关编辑按钮
    const isHideEditButton = useMemo(() => {
        return curBrushType === MOUSE_EVENT_TYPE.NONE && !isPhotoRemoveBg;
    }, [curBrushType, isPhotoRemoveBg]);

    const hasUndo = useMemo(() => hasBrushUndo && isShowEditCanvas, [hasBrushUndo, isShowEditCanvas]);
    const hasRedo = useMemo(() => hasBrushRedo && isShowEditCanvas, [hasBrushRedo, isShowEditCanvas]);
    const disableReset = useMemo(() => _disableReset || !isShowEditCanvas, [_disableReset, isShowEditCanvas]);

    const _setZoom = useCallback(
        (zoom, e, isFitClick = false) => {
            FJViewCanvasSingleton.setZoom(zoom, e, { isFitClick });
            dispatch(FJActionBrushEditCommon.updateZoomAction());
            dispatch(FJActionBrushEditCommon.updateSlideCompareDataAction());
        },
        [dispatch],
    );

    const undoHandler = useCallback(() => {
        if (hasUndo) {
            dispatch(FJActionBrushEditCommon.undoAction());
        }
    }, [hasUndo, dispatch]);

    const redoHandler = useCallback(() => {
        if (hasRedo) {
            dispatch(FJActionBrushEditCommon.redoAction());
        }
    }, [hasRedo, dispatch]);

    const changeIsMouseMove = useCallback(
        bool => {
            dispatch(setSliceIsMouseMove(bool));
        },
        [dispatch],
    );

    const resetHandler = useCallback(() => {
        if (disableReset) return;
        dispatch(FJActionBrushEditCommon.resetMaskAction());
        dispatch(FJActionBrushEditCommon.updateBrushUndoRedoAction());
    }, [disableReset, dispatch]);

    const compareHandler = useCallback(
        bool => {
            if (disableCompare) return;
            if (appType === FJAppConstant.PHOTO_RESTORATION || appType === FJAppConstant.PHOTO_COLORIZE) {
                // 滑动对比模式下，鼠标抬起时不结束滑动对比
                bool && dispatch(FJActionBrushEditCommon.enterOrEndSlideCompareAction(!isSlideCompare));
            } else {
                FJViewCanvasSingleton.compare(bool);
            }
        },
        [disableCompare, dispatch, isSlideCompare, appType],
    );

    const fitZoomAndPositionHandler = useCallback(() => {
        dispatch(FJActionBrushEditCommon.fitZoomAndPositionAction());
        dispatch(FJActionBrushEditCommon.updateSlideCompareDataAction());
    }, [dispatch]);

    const resizeCanvas = useCallback(() => {
        FJViewCanvasSingleton.resize();
        dispatch(FJActionBrushEditCommon.updateZoomAction());
        dispatch(FJActionBrushEditCommon.updateDisableFitButtonAction());
        dispatch(FJActionBrushEditCommon.updateSlideCompareDataAction());
    }, [dispatch]);

    const updateBrushSize = useCallback(
        (isAdd = false) => {
            if (curBrushType !== MOUSE_EVENT_TYPE.MASK_DRAW && curBrushType !== MOUSE_EVENT_TYPE.MASK_ERASE) {
                return;
            }

            const newBrushSize = isAdd ? Math.min(brushSize + 2, 100) : Math.max(brushSize - 2, 2);
            dispatch(FJActionBrushEditCommon.updateBrushSizeAction(Math.round(newBrushSize)));
        },
        [dispatch, brushSize, curBrushType],
    );

    return (
        <FJEditFloatTool
            zoom={zoom}
            hasUndo={hasUndo}
            hasRedo={hasRedo}
            disableReset={disableReset}
            isMouseMove={isMouseMove}
            disableCompare={disableCompare || !isShowEditCanvas}
            isHideEditButton={isHideEditButton}
            setZoom={_setZoom}
            undoHandler={undoHandler}
            redoHandler={redoHandler}
            resetHandler={resetHandler}
            changeIsMouseMove={changeIsMouseMove}
            compareHandler={compareHandler}
            fitZoomAndPositionHandler={fitZoomAndPositionHandler}
            resizeCanvas={resizeCanvas}
            updateBrushSize={updateBrushSize}
            setMouseInToolPanel={setMouseInToolPanel}
            isShowEditCanvas={isShowEditCanvas}
            disableFitButton={disableFitButton}
            isSlideCompare={isSlideCompare}
        />
    );
});

FJEditFloatToolContainer.propTypes = {
    setMouseInToolPanel: PropTypes.func.isRequired,
    isShowEditCanvas: PropTypes.bool.isRequired,
};

export default FJEditFloatToolContainer;
